import { ReactElement, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLangContext } from "context/Lang";
import { AsyncProcess } from "services/AsyncProcess";
import { routes } from "routes";
import { setToast } from "storeRedux/slices/toast";
import { DataTable, TableCol } from "components/DataTable/DataTable";
import { Button } from "components/Button/Button";
import { UserCheck, Users, XIcon } from "lucide-react";
import { Loader } from "components/Loader/Loader";


type ResponsiblePageProps = {
    onChangeResponsibles: (e: number[]) => void,
    responsiblesIds?: number[]
}

export const ResponsiblePage = ({ onChangeResponsibles, responsiblesIds: tempResponsiblesIds }: ResponsiblePageProps): ReactElement => {
    const { gW } = useLangContext();
    const dispatch = useDispatch();

    const [responsiblesIds, setResponsiblesIds] = useState<(number | undefined)[]>(tempResponsiblesIds || []);

    const [loading, setLoading] = useState<boolean>(false);
    const [loadingPutResponsible, setLoadingPutResponsible] = useState<boolean>(false);
    const [users, setUsers] = useState<UserType[]>([]);
    const fetchResponsibles = () => {
        setLoading(true);
        AsyncProcess({
            type: "get",
            url: `${routes.api.users}`,
            setError: (err) => {
                setLoading(false);
                dispatch(setToast({
                    detail: gW(err?.error || "happen-error"),
                    severity: "error",
                }));
            },
            effectProcess: [(res) => {
                setLoading(false);
                if (res?.success !== false) {
                    setUsers((res.data || [])?.map((f: UserType) => {
                        f.name = f.name + " " + f.surname;
                        return f;
                    }));
                }
            }]
        });
    };

    useEffect(() => {
        fetchResponsibles();
    }, []);



    const handleSelectResponsible = (user: UserType) => {

        if (!user.id) return;

        if (responsiblesIds.includes(user.id)) {
            dispatch(setToast({ detail: gW("already-selected"), severity: "warn" }));
            return;
        }

        setResponsiblesIds(prev => [...prev, user.id]);

        onChangeResponsibles([...responsiblesIds, user.id]?.filter(r => r !== undefined) as number[]);
    };

    const handleRemoveResponsible = (id: number) => {

        if (tempResponsiblesIds?.includes(id)) {
            setLoadingPutResponsible(true);
            AsyncProcess({
                type: "post",
                url: routes.api.responsible + "/passive/" + id,
                setError: (err) => {
                    setLoadingPutResponsible(false);
                    dispatch(setToast({
                        detail: gW(err?.error || "happen-error"),
                        severity: "error",
                    }));
                },
                effectProcess: [(res) => {
                    if (res?.success === false) {
                        dispatch(setToast({
                            detail: gW(res?.message || "happen-error"),
                            severity: "error",
                        }));
                    } else {
                        setResponsiblesIds(prev => prev.filter(item => item !== id));



                        onChangeResponsibles([...responsiblesIds || []]?.filter(r => r !== id) as number[])
                    }


                    setLoadingPutResponsible(false);
                }]
            })
        } else {
            setResponsiblesIds(prev => prev.filter(item => item !== id));

            onChangeResponsibles([...responsiblesIds || []]?.filter(r => r !== id) as number[])
        }
    };






    const columns: TableCol<UserType>[] = [
        {
            label: gW("name"),
            name: "name",
            cell: (user) => (
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-bold text-xs">
                        {user.name?.[0]}{user.surname?.[0]}
                    </div>
                    <span>{user.name} </span>
                </div>
            )
        },
        {
            label: gW("email"),
            name: "email"
        },
        {
            label: gW("actions"),
            cell: (user) => (
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                        handleSelectResponsible(user);

                    }}
                    className="gap-2"
                >
                    <UserCheck size={14} />
                    {gW("select")}
                </Button>
            )
        }
    ];



    const selectedResponsibles = users?.filter(u => responsiblesIds?.includes(u?.id));

    return (
        <div className="flex flex-col gap-4">

            <div className="flex flex-col gap-2">
                <h3 className="text-sm font-semibold flex items-center gap-2 text-gray-700">
                    <Users size={16} />
                    {gW("selected-responsibles")} ({selectedResponsibles.length})
                    {loadingPutResponsible && <Loader />}
                </h3>
                <div className="flex flex-wrap   gap-2 min-h-[40px] p-3 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                    {selectedResponsibles.length === 0 ? (
                        <span className="text-xs text-gray-400 italic">{gW("no-selection-yet")}</span>
                    ) : (
                        selectedResponsibles.map(r => (
                            <div key={r.id} className="flex items-center gap-2 bg-white border px-3 py-1.5 rounded-full shadow-sm animate-in fade-in zoom-in duration-200">
                                <span className="text-sm font-medium">{r.name}</span>
                                <button
                                    onClick={() => r.id && handleRemoveResponsible(r.id)}
                                    className="text-gray-400 hover:text-red-500 transition-colors"
                                >
                                    <XIcon size={14} />
                                </button>
                            </div>
                        ))
                    )}


                </div>
            </div>
            <div className="border rounded-xl overflow-hidden bg-white">
                <DataTable
                    data={users?.filter(u => !responsiblesIds?.includes(u.id))}
                    columns={columns}
                    loading={loading}
                    rowKey={(user) => (user?.id || "")?.toString()}
                    rows={5} // عرض عدد أقل داخل المودال
                />
            </div>

        </div>
    );
};